import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonService } from 'src/app/services/common-services/common.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import {
  FormGroup,
  FormControl,
  FormBuilder,
  Validators,
} from '@angular/forms';
import * as EmailValidator from 'email-validator';
import { noNumericAfterDotComValidator } from 'src/app/shared/numeric-after-dot-validator';
import { CartService } from '../../../services/cart.service';
import { UserType } from 'src/app/shared/constants/constants';
import { ConfirmationBoxComponent } from 'src/app/shared/elements/confirmation-box/confirmation-box.component';
import { MatDialog } from '@angular/material/dialog';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { getCaptcha, isLocalEnv } from 'src/app/shared/utils';

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
})
export class LoginFormComponent implements AfterViewInit {
  @ViewChild('my_captcha_container', { static: false }) captchaContainer!: ElementRef;
  captchaElementContainer: any;

  baseUrl = environment.BASE_URL;
  portalUrl: string = environment.PORTAL_URL;
  captchaKey = environment.CAPTCHA_API;

  loginForm!: FormGroup;
  rememberMe: FormControl = new FormControl('');
  hide = true;
  urlParams: any;
  companyid!: number;
  isCustomer: boolean = true;
  userType!: UserType;
  preEmailid: string | null = null;
  isReadOnly = true;
  isDashboardRedirect: boolean = false;
  showLoginButton: boolean = true;
  loginError: string = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private commonService: CommonService,
    private cartService: CartService,
    private toastr: ToastrService,
    private fb: FormBuilder,
    public dialog: MatDialog,
    public gtmService: GoogleTagManagerService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const token = params['token'];
      const refreshToken = params['refreshToken'];
      const accessToken = params['accessToken'];
      let dashboard = params['dashboard'];
      const customercompanyId = params['customercompanyId'];
      const customeruserId = params['customeruserId'];
      const customerEmail = params['email'];
      if (customerEmail) {
        localStorage.removeItem('preEmail');
        localStorage.setItem('preEmail', customerEmail);
      }
      if (dashboard) {
        this.isDashboardRedirect = true;
      }
      const checkToken: any = localStorage.getItem('customerToken');
      if (token && refreshToken && accessToken && !checkToken) {
        this.setTokenData(
          token,
          refreshToken,
          accessToken,
          customeruserId,
          customercompanyId
        );
      } else if (checkToken) {
        this.openConfirmation();
      }
    });

    this.preEmailid = localStorage.getItem('preEmail');
    this.rememberEmailAndPassword();
    this.tokenInfo();
    this.getUrlData();
    this.createLoginForm();
  }

  ngAfterViewInit(): void {
    this.captchaElementContainer = this.captchaContainer?.nativeElement;
  }

  createCaptcha(res: any) {
    this.showLoginButton = false;
    const captchaElement = getCaptcha(this.captchaElementContainer, this.captchaKey);
    captchaElement.then((response: boolean) => {
      if (response) {
        this.setUserInfoAndGetPermissions(res);
      } else {
        this.loginError = 'Invalid captcha. Please try again.';
        this.createCaptcha(res); // retry captcha
      }
    });
  }

  onSubmit() {
    this.loginError = '';
    const APIUrl = `${this.baseUrl}auth-service/login`;
    const payload = {
      userName: this.loginForm.value.email,
      email: this.loginForm.value.email,
      passWord: this.commonService.encryptData(this.loginForm.value.passWord),
    };

    this.commonService.postDatawithoutHeaders(APIUrl, payload).subscribe({
      next: (res: any) => {
        if (isLocalEnv()) {
          // this.getPermissionsData(userId);
          this.setUserInfoAndGetPermissions(res);
        } else {
          this.createCaptcha(res);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.loginError = error.error.status.error.message;
      },
    });
  }

  setUserInfoAndGetPermissions(res: any) {
    const token = res?.response?.idToken;
    localStorage.setItem('customerToken', token);
    const jwt = token;
    const tokens = jwt.split('.');
    const idToken = JSON.parse(atob(tokens[1]));
    const customRole = idToken['custom:role'];
    const userId = idToken['custom:userid'];
    const companyId = idToken['custom:companyId'];

    localStorage.setItem('customeruserId', userId);
    localStorage.setItem('customercompanyId', companyId);
    localStorage.setItem('customerAccessToken', res.response.accessToken);
    localStorage.setItem('customerRefreshToken', res.response.refreshToken);
    localStorage.setItem('customerRole', customRole);

    this.companyid = companyId;

    this.tokenInfo();
    this.pushLoginEvent(customRole, userId, companyId);
    this.getPermissionsData(userId);
  }

  createLoginForm() {
    const email = localStorage.getItem('customeruserEmail');
    const password = localStorage.getItem('customerpassword');
    this.loginForm = this.fb.group({
      email: new FormControl(this.preEmailid || '', [
        Validators.required,
        Validators.email,
        noNumericAfterDotComValidator(),
      ]),
      passWord: new FormControl(password || '', [Validators.required]),
    });
  }

  rememberEmailAndPassword() {
    this.rememberMe.valueChanges.subscribe((res: any) => {
      if (res) {
        localStorage.setItem('customeruserEmail', this.loginForm.value.email);
        localStorage.setItem('customerpassword', this.loginForm.value.passWord);
      } else {
        localStorage.removeItem('customeruserEmail');
        localStorage.removeItem('customerpassword');
      }
    });
  }

  openConfirmation() {
    const dialogRef = this.dialog.open(ConfirmationBoxComponent, {
      disableClose: true,
      data: {
        cssClass: 'redirectCnt',
        content: `You are already login with other user. If you want proceed then logout first.`,
        btnConfirm: 'OK',
        btnCancel: 'Cancel',
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.router.navigateByUrl('/');
      }
    });
  }

  getUrlData(): void {
    this.urlParams = this.commonService.getUrlParams();
  }

  tokenInfo() {
    const token = localStorage.getItem('customerToken');
    const companyId = localStorage.getItem('customercompanyId');
    const userId = localStorage.getItem('customeruserId');
    const permissions = localStorage.getItem('customerpermissionsData');

    if (token && companyId && permissions && userId) {
      this.router.navigateByUrl('/');
    }
  }

  getPermissionsData(userId: any) {
    const apiURL = `${this.baseUrl}auth-service/role-permission-management/${userId}?userRole=Customer`;
    this.commonService.getData(apiURL).subscribe({
      next: (res: any) => {
        this.chekCartData();
        localStorage.removeItem('cartData');
        if (res) {
          localStorage.setItem('customerpermissionsData', JSON.stringify(res));
          this.cartService.getCartItems(this.companyid);
          this.commonService.checkToken();
          this.router.navigateByUrl(this.isDashboardRedirect ? '/dashboard' : '/');
        }
      },
      error: (err: HttpErrorResponse) => {
        console.error(err);
      },
    });
  }

  chekCartData() {
    const cartLocalData: any = localStorage.getItem('cartData');
    if (cartLocalData) {
      const dataItem = JSON.parse(cartLocalData);
      Object.values(dataItem).forEach((product: any) => this.addToCart(product));
    }
  }

  addToCart(product: any) {
    const payload = {
      productId: product.productId,
      productName: product.productName,
      quantity: product.quantity,
      date: product.date,
      cmsCatName: product.cmsCatName,
      cmsSubCatName: product.cmsSubCatName,
      companyId: this.companyid,
      imageUrl: product.imageUrl,
      rfqTemplate: product.rfqTemplate,
      tags: product.tags,
    };

    const apiUrl = `${this.baseUrl}rfq/cart/addCart`;
    this.commonService.postDatawithoutHeaders(apiUrl, payload).subscribe(() => {
      this.cartService.getCartItems(this.companyid);
    });
  }

  togglePasswordVisibility() {
    this.hide = !this.hide;
  }

  isValid() {
    return this.loginForm.invalid;
  }

  get validateEmail() {
    return EmailValidator.validate(this.loginForm.value.email);
  }

  setTokenData(
    token: any,
    refreshToken: any,
    accessToken: any,
    userId: any,
    companyId: any
  ) {
    localStorage.setItem('customerToken', token);
    localStorage.setItem('customerAccessToken', accessToken);
    localStorage.setItem('customerRefreshToken', refreshToken);
    localStorage.setItem('customerRole', 'Customer');
    localStorage.setItem('customeruserId', userId);
    localStorage.setItem('customercompanyId', companyId);

    this.companyid = companyId;
    this.getPermissionsData(userId);
  }

  pushLoginEvent(customRole: any, userId: any, companyId: any) {
    const gtmTag = {
      event: 'Customer_Login',
      event_category: 'User Input',
      login_role_customer: customRole,
      login_userId_customer: userId,
      login_companyId_customer: companyId,
      login_email_customer: localStorage.getItem('preEmail'),
    };
    this.gtmService.pushTag(gtmTag);
  }

  redirectToForgotPassword(): void {
    this.router.navigateByUrl('/login/forget-password');
  }
}
